import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenu } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { IConversation, IConversationUpdate, ConversationClassification, ConversationMenuOptions, ConversationStatus } from '../../../models/conversation.models';
import { IUser } from 'src/app/features/settings/models/settings.models';
import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { CONVERSATION_CATEGORIES, CONVERSATION_STATUSES } from '../../../data/conversation.data';
import { getUserInitials, getUserFullname, getUserColor } from '../../../utils/user.util';
import { InboxConfirmationDialogComponent, InboxConfirmationSkipType } from '../../inbox-confirmation-dialog/inbox-confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { InboxConfirmationActionType } from '../../../models/inbox.models';
import { MailboxType } from '../../../models/account.models';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';

// ─────────────────────────────────────────────────────────────────────────────
// ConversationActionMenuComponent
// ─────────────────────────────────────────────────────────────────────────────
// A reusable <mat-menu> that shows one of three panels depending on which
// action the user triggered:
//
//   • CATEGORY  – pick a classification (order, rfq, support, …)
//   • STATUS    – pick a status (new, open, on_hold, closed)
//   • ASSIGN    – pick a team member or unassign
//
// The menu instance is exposed via `@ViewChild` so the parent can wire
// `[matMenuTriggerFor]="conversationActionMenu.menu"` on any trigger element.
//
// Users are fetched directly from the NgRx store (the parent already
// dispatches the FetchAllUsers action at init time).
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-conversation-action-menu',
  standalone: true,
  templateUrl: './conversation-action-menu.component.html',
  styleUrls: ['./conversation-action-menu.component.scss'],
  imports: [CommonModule, UiModule, SharedModule],
})
export class ConversationActionMenuComponent implements OnInit, OnDestroy {
  // ── Inputs ──────────────────────────────────────────────────────────────

  /** Which panel to show inside the menu (category | status | assign) */
  @Input() activeMenu: ConversationMenuOptions | null = null;

  /** Whether we're updating a single conversation or a bulk selection */
  @Input() updateType: 'single' | 'bulk' = 'single';

  // ── Static reference data (owned by this component) ─────────────────────

  /** Available classification categories */
  readonly categories: { name: string; type: ConversationClassification }[] = CONVERSATION_CATEGORIES;

  /** Available conversation statuses */
  readonly statuses: { name: string; type: ConversationStatus; icon: string }[] = CONVERSATION_STATUSES;

  /** The currently-selected conversation (used to show check marks in single mode) */
  @Input() activeConversation: IConversation | null = null;

  // ── Outputs ─────────────────────────────────────────────────────────────

  /** Emits the partial update payload when the user picks an option */
  @Output() updated = new EventEmitter<IConversationUpdate>();
  @Output() markAsUnread = new EventEmitter<void>();

  // ── Public menu reference ───────────────────────────────────────────────
  /** Expose the MatMenu so parents can bind [matMenuTriggerFor] */
  @ViewChild('conversationMenu', { static: true }) menu!: MatMenu;

  // ── Users (from store) ────────────────────────────────────────────────
  /** All platform users (fetched from the NgRx store) */
  users: IUser[] = [];
  /** Connected user id (for assign-to-self confirmation) */
  private currentUserId: number | null = null;
  private users$ = this.store.select((state) => state.settings.allUsers);
  private destroy$ = new Subject<void>();

  // ── Constants (exposed to template) ─────────────────────────────────────
  CONVERSATION_MENU_OPTIONS = ConversationMenuOptions;

  // ── Readonly refs ─────────────────────────────────────────────────────────

  readonly getUserInitials = getUserInitials;
  readonly getUserFullname = getUserFullname;
  readonly getUserColor = getUserColor;

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    const currentUser = this.authService.getCurrentUser();
    this.currentUserId = currentUser?.id ?? null;
    this.users$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
      this.users = users;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── User helpers ────────────────────────────────────────────────────────

  updateAssignee(assigneeId: number | null): void {
    // ── Update assignee ────────────────────────────────────────────────────
    // ── On unassign, emit and return. On bulk, emit and return.
    // ── On single: same assignee → skip. Assign to connected user / team / already has assignee → update without confirmation.
    // ── Personal unassigned → show confirmation unless skipped.
    // ─────────────────────────────────────────────────────────────────────────────

    if (assigneeId === null) {
      this.updated.emit({ assignee_id: assigneeId });
      return;
    }

    if (this.updateType === 'bulk') {
      this.updated.emit({ assignee_id: assigneeId });
      return;
    }

    if (this.updateType === 'single') {
      if (this.activeConversation?.assignee && assigneeId === this.activeConversation.assignee.id) return;

      // Assigning to connected user: update without confirmation
      if (this.currentUserId != null && assigneeId === this.currentUserId) {
        this.updated.emit({ assignee_id: assigneeId });
        return;
      }

      // Team conversation: update without confirmation
      if (this.activeConversation?.mailbox?.type === MailboxType.TEAM) {
        this.updated.emit({ assignee_id: assigneeId });
        return;
      }

      // Thread already has an assignee: update without confirmation
      if (this.activeConversation?.assignee?.id) {
        this.updated.emit({ assignee_id: assigneeId });
        return;
      }

      // Personal, unassigned conversation: show confirmation unless user chose "Don't show again"
      if (!this.activeConversation?.assignee && this.activeConversation?.mailbox?.type === MailboxType.PERSONAL) {
        if (InboxConfirmationDialogComponent.shouldSkipConfirmation(InboxConfirmationSkipType.SHARE_PERSONAL_CONVERSATION)) {
          this.updated.emit({ assignee_id: assigneeId });
        } else {
          this.openShareConfirmationDialog(assigneeId);
        }
        return;
      }
    }
  }

  private openShareConfirmationDialog(assigneeId: number): void {
    if (!this.activeConversation) return;
    const dialogRef = this.dialog.open(InboxConfirmationDialogComponent, {
      data: { actionType: InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION, inboxId: this.activeConversation.id },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.updated.emit({ assignee_id: assigneeId });
      }
    });
  }
}
